import React, { useState, useRef } from 'react';
import {
  FileText, Upload, Users, Building, User as UserIcon, Send, X, Plus, Trash2,
  Check, Search, Loader2, ArrowLeft, ArrowRight, Archive, Mail, Phone, Sparkles
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Modtager', 'Upload', 'Underskrivere', 'Send'];

// ─── Genbrugelige byggeklodser ────────────────────────────────────────────────

function StepHeader({ step }) {
  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap mb-8">
      {STEPS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <React.Fragment key={label}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                active ? 'bg-brand-600 text-white font-medium'
                : done ? 'text-th-text-secondary'
                : 'text-th-text-faint'
              }`}
            >
              <span
                className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold shrink-0 ${
                  active ? 'bg-white/25 text-white'
                  : done ? 'bg-green-600 text-white'
                  : 'bg-th-hover text-th-text-faint'
                }`}
              >
                {done ? <Check size={12} strokeWidth={3} /> : i + 1}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <span className={`hidden sm:block w-5 h-px ${done ? 'bg-green-600' : 'bg-th-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Kort med ikon, titel og undertekst — som i mockup'et. */
function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-th-hover border border-th-border flex items-center justify-center shrink-0">
          <Icon size={18} className="text-th-text-secondary" />
        </div>
        <div className="min-w-0 pt-0.5">
          <h3 className="text-th-text font-semibold leading-tight">{title}</h3>
          {subtitle && <p className="text-sm text-th-text-muted mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="bg-th-card border border-th-border rounded-xl p-4 sm:p-5">
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, optional, children, hint }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="block text-sm font-medium text-th-text-secondary">{label}</label>
        {required && <span className="text-xs text-th-text-faint">Påkrævet</span>}
        {optional && <span className="text-xs text-th-text-faint">Valgfrit</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-th-text-muted mt-1">{hint}</p>}
    </div>
  );
}

const inputCls = 'bg-th-input border-th-border-light text-th-text';

/** Valg mellem to muligheder — som "Privat person / Selskab" i mockup'et. */
function ChoiceRow({ options, value, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {options.map(o => {
        const on = value === o.value;
        return (
          <button
            key={o.value} type="button" onClick={() => onChange(o.value)}
            className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
              on ? 'border-brand-500 bg-brand-500/5' : 'border-th-border bg-th-input hover:border-th-border-hover'
            }`}
          >
            <span className="min-w-0">
              <span className={`block text-sm font-medium ${on ? 'text-th-text' : 'text-th-text-secondary'}`}>
                {o.label}
              </span>
              {o.hint && <span className="block text-xs text-th-text-muted mt-0.5">{o.hint}</span>}
            </span>
            <span
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                on ? 'border-brand-500 bg-brand-500' : 'border-th-border'
              }`}
            >
              {on && <Check size={12} className="text-white" strokeWidth={3} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PhoneInput({ value, onChange, placeholder = '20 31 20 00' }) {
  return (
    <div className="flex">
      <span className="inline-flex items-center gap-1.5 px-3 rounded-l-lg border border-r-0 border-th-border-light bg-th-hover text-sm text-th-text-secondary shrink-0">
        🇩🇰 +45
      </span>
      <Input
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className={`${inputCls} rounded-l-none`}
      />
    </div>
  );
}

// ─── Wizard ───────────────────────────────────────────────────────────────────

function DocumentWizard({ onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Trin 1 — modtager/part
  const [partyType, setPartyType] = useState('company');   // company | person
  const [cvr, setCvr] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [address2, setAddress2] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [city, setCity] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  // Trin 2 — dokument
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('sign');
  const [kraevMitid, setKraevMitid] = useState(false);
  const [returUrl, setReturUrl] = useState('');
  const [fil, setFil] = useState(null);
  const fileRef = useRef(null);

  // Trin 3 — underskrivere
  const [signers, setSigners] = useState([
    { firstName: '', lastName: '', email: '', phone: '', cvr: '' },
  ]);

  const [udlaeser, setUdlaeser] = useState(false);

  // Parterne står som regel i kontrakten. Vi læser dem ud og udfylder felterne
  // — afsenderen retter og godkender ved at sende. Filen sendes fra browseren,
  // fordi dokumentet først oprettes, når guiden er færdig.
  const udlaesUnderskrivere = async () => {
    if (!fil) { toast.error('Upload dokumentet først'); return; }
    setUdlaeser(true);
    try {
      const fd = new FormData();
      fd.append('fil', fil);
      const r = await axios.post(`${API}/documents/udlaes-underskrivere`, fd);
      const fundet = r.data?.underskrivere || [];
      if (!fundet.length) { toast.error('Fandt ingen underskrivere i dokumentet'); return; }
      const nye = fundet.map(u => {
        const dele = (u.navn || '').trim().split(/\s+/);
        return {
          firstName: dele[0] || '',
          lastName: dele.slice(1).join(' '),
          email: u.email || '',
          phone: u.telefon || '',
          cvr: u.cvr || '',
        };
      });
      // Det afsenderen selv har skrevet, bliver stående — forslaget lægges til.
      setSigners(gamle => {
        const udfyldte = gamle.filter(s => s.firstName.trim() || s.lastName.trim() || s.email.trim());
        const kendt = new Set(udfyldte.map(s => `${s.firstName} ${s.lastName}`.trim().toLowerCase()));
        const ekstra = nye.filter(s => !kendt.has(`${s.firstName} ${s.lastName}`.trim().toLowerCase()));
        return [...udfyldte, ...ekstra];
      });
      toast.success(`Fandt ${fundet.length} underskriver${fundet.length === 1 ? '' : 'e'} — tjek felterne`);
    } catch (e) {
      const besked = e.response?.status === 503
        ? 'Automatisk udlæsning er ikke slået til på denne server'
        : (e.response?.data?.detail || 'Kunne ikke udlæse fra dokumentet');
      toast.error(besked);
    } finally {
      setUdlaeser(false);
    }
  };

  const setSigner = (i, patch) =>
    setSigners(list => list.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSigner = () =>
    setSigners(list => [...list, { firstName: '', lastName: '', email: '', phone: '', cvr: '' }]);
  const removeSigner = i => setSigners(list => list.filter((_, j) => j !== i));

  const lookupCvr = async () => {
    const clean = cvr.replace(/\D/g, '');
    if (clean.length !== 8) { toast.error('CVR skal være 8 cifre'); return; }
    setLookingUp(true);
    try {
      const r = await axios.get(`${API}/documents/lookup-cvr`, { params: { cvr: clean } });
      if (r.data?.name) {
        setCompanyName(r.data.name);
        setAddress(r.data.address || '');
        setZipcode(r.data.zipcode || '');
        setCity(r.data.city || '');
        if (r.data.phone) setCompanyPhone(r.data.phone);
        toast.success(`Hentet: ${r.data.name}`);
      } else {
        toast.error('Selskab ikke fundet');
      }
    } catch {
      toast.error('Kunne ikke slå CVR op');
    } finally {
      setLookingUp(false);
    }
  };

  const returUrlOk = !returUrl.trim() || /^https:\/\/.+/i.test(returUrl.trim());

  const canNext = () => {
    if (step === 0) {
      return partyType === 'person' || (companyName.trim().length > 1);
    }
    // Retur-URL'en afvises alligevel af serveren, men brugeren skal opdage
    // fejlen her — ikke efter at have udfyldt hele resten af guiden.
    if (step === 1) return !!fil && title.trim().length > 1 && returUrlOk;
    if (step === 2) {
      if (purpose === 'archive') return true;
      return signers.length > 0 && signers.every(s =>
        s.firstName.trim() && s.lastName.trim() &&
        s.email.includes('@') && s.phone.replace(/\D/g, '').length >= 8);
    }
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      // 1. Opret dokumentet
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('purpose', purpose);
      // Multipart har ingen boolean-type — serveren læser "true"/"" som ja/nej.
      fd.append('kraev_mitid', purpose === 'sign' && kraevMitid ? 'true' : '');
      if (purpose === 'sign' && returUrl.trim()) fd.append('retur_url', returUrl.trim());
      if (partyType === 'company' && cvr.replace(/\D/g, '').length === 8) {
        fd.append('cvr', cvr.replace(/\D/g, ''));
      }
      fd.append('fil', fil);
      const created = await axios.post(`${API}/documents`, fd);
      const docId = created.data?.id;
      if (!docId) throw new Error('Intet dokument-id');

      // 2. Tilføj underskrivere (kun ved underskrift)
      if (purpose === 'sign') {
        for (const s of signers) {
          await axios.post(`${API}/documents/${docId}/signers`, {
            name: `${s.firstName.trim()} ${s.lastName.trim()}`.trim(),
            email: s.email.trim().toLowerCase(),
            phone: s.phone.trim(),
            cvr: (s.cvr || (partyType === 'company' ? cvr : '')).replace(/\D/g, '') || null,
          });
        }
        // 3. Send
        const sent = await axios.post(`${API}/documents/${docId}/send`);
        toast.success(`Sendt til ${sent.data?.sent ?? signers.length} underskriver(e)`);
      } else {
        toast.success('Dokument arkiveret');
      }
      onDone(docId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Noget gik galt');
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    // Ved arkivering springes underskriver-trinnet over
    if (step === 1 && purpose === 'archive') { setStep(3); return; }
    if (step === 3) { submit(); return; }
    setStep(step + 1);
  };
  const back = () => {
    if (step === 3 && purpose === 'archive') { setStep(1); return; }
    setStep(Math.max(0, step - 1));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onClose}
          className="text-sm text-th-text-secondary hover:text-th-text inline-flex items-center gap-1.5 transition-colors">
          <ArrowLeft size={15} /> Tilbage til dokumenter
        </button>
        <button onClick={onClose} className="text-th-text-muted hover:text-th-text transition-colors">
          <X size={20} />
        </button>
      </div>

      <StepHeader step={step} />

      {/* ── Trin 1: Modtager/part ── */}
      {step === 0 && (
        <>
          <Section icon={UserIcon} title="Hvem gælder dokumentet?"
            subtitle="Er modparten en privatperson eller et selskab?">
            <ChoiceRow
              value={partyType} onChange={setPartyType}
              options={[
                { value: 'person', label: 'Privatperson', hint: 'En eller flere personer' },
                { value: 'company', label: 'Selskab', hint: 'Firma med CVR-nummer' },
              ]}
            />
          </Section>

          {partyType === 'company' && (
            <Section icon={Building} title="Selskabsinformation"
              subtitle="Oplysningerne hentes automatisk fra CVR">
              <div className="space-y-4">
                <Field label="CVR" required>
                  <div className="flex gap-2">
                    <Input
                      value={cvr} maxLength={8} placeholder="12345678"
                      onChange={e => setCvr(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={e => e.key === 'Enter' && lookupCvr()}
                      className={inputCls}
                    />
                    <button type="button" onClick={lookupCvr} disabled={lookingUp}
                      className="shrink-0 inline-flex items-center gap-1.5 px-4 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors disabled:opacity-50">
                      {lookingUp ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                      <span className="hidden sm:inline">Hent info fra CVR</span>
                    </button>
                  </div>
                </Field>

                <Field label="Selskabsnavn" required>
                  <Input value={companyName} onChange={e => setCompanyName(e.target.value)}
                    placeholder="Firmanavn ApS" className={inputCls} />
                </Field>

                <Field label="Adresse">
                  <Input value={address} onChange={e => setAddress(e.target.value)}
                    placeholder="Vejnavn 1" className={`${inputCls} mb-2`} />
                  <Input value={address2} onChange={e => setAddress2(e.target.value)}
                    placeholder="Adresse linje 2…" className={inputCls} />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Postnummer">
                    <Input value={zipcode} onChange={e => setZipcode(e.target.value)}
                      placeholder="6000" className={inputCls} />
                  </Field>
                  <Field label="By">
                    <Input value={city} onChange={e => setCity(e.target.value)}
                      placeholder="Kolding" className={inputCls} />
                  </Field>
                </div>

                <Field label="Telefon" optional>
                  <PhoneInput value={companyPhone} onChange={setCompanyPhone} />
                </Field>
              </div>
            </Section>
          )}
        </>
      )}

      {/* ── Trin 2: Upload ── */}
      {step === 1 && (
        <>
          <Section icon={FileText} title="Dokumentet"
            subtitle="Hvad skal der ske med dokumentet?">
            <div className="space-y-4">
              <Field label="Titel" required>
                <Input autoFocus value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="fx Samarbejdsaftale" className={inputCls} />
              </Field>
              <Field label="Formål">
                <ChoiceRow
                  value={purpose} onChange={setPurpose}
                  options={[
                    { value: 'sign', label: 'Til underskrift', hint: 'Sendes til godkendelse' },
                    { value: 'archive', label: 'Kun arkivering', hint: 'Gemmes uden underskrift' },
                  ]}
                />
              </Field>
              {purpose === 'sign' && (
                <Field label="Sådan bekræfter underskriveren sig">
                  <ChoiceRow
                    value={kraevMitid ? 'mitid' : 'sms'}
                    onChange={v => setKraevMitid(v === 'mitid')}
                    options={[
                      { value: 'sms', label: 'E-mail + SMS', hint: 'Bekræfter et mobilnummer' },
                      { value: 'mitid', label: 'MitID', hint: 'Bekræfter hvem personen er' },
                    ]}
                  />
                </Field>
              )}
              {purpose === 'sign' && (
                <Field label="Send underskriveren videre bagefter (valgfrit)">
                  <Input
                    value={returUrl}
                    onChange={e => setReturUrl(e.target.value)}
                    placeholder="https://ditfirma.dk/tak"
                    className={inputCls}
                  />
                  <p className={`text-xs mt-1 ${returUrlOk ? 'text-th-text-muted' : 'text-red-500'}`}>
                    Kvitteringen får en knap, der fører hertil. Skal være en fuld https-adresse.
                  </p>
                </Field>
              )}
            </div>
          </Section>

          <Section icon={Upload} title="Upload fil" subtitle="PDF anbefales">
            <div
              onClick={() => fileRef.current?.click()}
              className={`flex items-center gap-3 px-4 py-5 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                fil ? 'border-brand-500 bg-brand-500/5' : 'border-th-border hover:border-brand-400 bg-th-input'
              }`}
            >
              <Upload size={20} className={fil ? 'text-brand-500' : 'text-th-text-muted'} />
              <div className="min-w-0">
                <p className={`text-sm truncate ${fil ? 'text-th-text font-medium' : 'text-th-text-muted'}`}>
                  {fil ? fil.name : 'Klik for at vælge fil'}
                </p>
                {fil && (
                  <p className="text-xs text-th-text-muted">
                    {(fil.size / 1024).toFixed(0)} KB
                  </p>
                )}
              </div>
              <input ref={fileRef} type="file" className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt"
                onChange={e => setFil(e.target.files[0] || null)} />
            </div>
          </Section>
        </>
      )}

      {/* ── Trin 3: Underskrivere ── */}
      {step === 2 && (
        <Section icon={Users} title="Underskrivere"
          subtitle="Hvem skal underskrive dokumentet?">
          <div className="space-y-4">
            <button
              type="button"
              onClick={udlaesUnderskrivere}
              disabled={udlaeser || !fil}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-brand-400 text-brand-500 hover:bg-brand-500/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <Sparkles size={16} />
              {udlaeser ? 'Læser dokumentet…' : 'Udlæs underskrivere fra dokumentet'}
            </button>
            <p className="text-xs text-th-text-muted -mt-2">
              Vi finder parterne i dokumentet og udfylder felterne. Tjek dem altid —
              e-mail og mobil står sjældent i en kontrakt.
            </p>
            {signers.map((s, i) => (
              <div key={i} className="rounded-xl border border-th-border bg-th-input p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <UserIcon size={15} className="text-th-text-muted" />
                    <span className="text-sm font-medium text-th-text">Underskriver #{i + 1}</span>
                    {i === 0 && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-th-hover text-th-text-muted">
                        Primær
                      </span>
                    )}
                  </div>
                  {signers.length > 1 && (
                    <button onClick={() => removeSigner(i)}
                      className="p-1.5 rounded-lg text-th-text-muted hover:text-red-500 hover:bg-th-hover transition-colors">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Fornavn" required>
                      <Input value={s.firstName} onChange={e => setSigner(i, { firstName: e.target.value })}
                        placeholder="Søren" className={inputCls} />
                    </Field>
                    <Field label="Efternavn" required>
                      <Input value={s.lastName} onChange={e => setSigner(i, { lastName: e.target.value })}
                        placeholder="Sundahl" className={inputCls} />
                    </Field>
                  </div>
                  <Field label="E-mail" required>
                    <Input type="email" value={s.email} onChange={e => setSigner(i, { email: e.target.value })}
                      placeholder="navn@firma.dk" className={inputCls} />
                  </Field>
                  <Field label="Mobilnummer" required
                    hint="Underskriveren skal indtaste præcis dette nummer for at kunne underskrive.">
                    <PhoneInput value={s.phone} onChange={v => setSigner(i, { phone: v })} />
                  </Field>
                </div>
              </div>
            ))}

            <button onClick={addSigner}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-dashed border-th-border hover:border-brand-400 bg-th-input transition-colors group">
              <span className="flex items-center gap-2 text-sm text-th-text-secondary">
                <UserIcon size={15} className="text-th-text-muted" />
                Tilføj en underskriver
              </span>
              <span className="w-7 h-7 rounded-lg bg-brand-600 group-hover:bg-brand-700 text-white flex items-center justify-center transition-colors">
                <Plus size={15} />
              </span>
            </button>
          </div>
        </Section>
      )}

      {/* ── Trin 4: Send ── */}
      {step === 3 && (
        <Section icon={purpose === 'archive' ? Archive : Send}
          title={purpose === 'archive' ? 'Klar til arkivering' : 'Klar til at sende'}
          subtitle={purpose === 'archive'
            ? 'Dokumentet gemmes uden underskrift'
            : 'Gennemgå før dokumentet sendes ud'}>
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-th-text-faint mb-1.5">Dokument</p>
              <p className="text-th-text font-medium">{title}</p>
              <p className="text-sm text-th-text-muted">{fil?.name} · {((fil?.size || 0) / 1024).toFixed(0)} KB</p>
            </div>

            {partyType === 'company' && companyName && (
              <div className="pt-3 border-t border-th-border-light">
                <p className="text-xs uppercase tracking-wider text-th-text-faint mb-1.5">Selskab</p>
                <p className="text-th-text">{companyName}</p>
                {cvr && <p className="text-sm text-th-text-muted">CVR {cvr}</p>}
                {(address || city) && (
                  <p className="text-sm text-th-text-muted">
                    {[address, address2, [zipcode, city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            )}

            {purpose === 'sign' && (
              <div className="pt-3 border-t border-th-border-light">
                <p className="text-xs uppercase tracking-wider text-th-text-faint mb-2">
                  Underskrivere ({signers.length})
                </p>
                <div className="space-y-2">
                  {signers.map((s, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-th-hover text-th-text-muted text-xs flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-th-text">{s.firstName} {s.lastName}</p>
                        <p className="text-xs text-th-text-muted flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Mail size={11} /> {s.email}</span>
                          <span className="inline-flex items-center gap-1"><Phone size={11} /> {s.phone}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-th-text-muted mt-3">
                  Hver underskriver får en e-mail med et personligt link. De skal gennemlæse
                  dokumentet, bekræfte deres mobilnummer og godkende via SMS.
                </p>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── Navigation ── */}
      <div className="flex items-center justify-between gap-3 mt-8">
        <button onClick={step === 0 ? onClose : back} disabled={busy}
          className="px-4 py-2.5 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors disabled:opacity-50">
          {step === 0 ? 'Annuller' : 'Tilbage'}
        </button>
        <button onClick={next} disabled={busy || !canNext()}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors disabled:opacity-40">
          {busy ? <><Loader2 size={15} className="animate-spin" /> Sender…</>
            : step === 3 ? <>{purpose === 'archive' ? 'Arkivér' : 'Send til underskrift'} <Send size={15} /></>
            : <>Næste <ArrowRight size={15} /></>}
        </button>
      </div>
    </div>
  );
}

export default DocumentWizard;
